/**
 * Earliest legal alcohol handover for a pickup order — staff-facing twin of
 * the storefront's apps/storefront/src/lib/earliest-pickup.ts. Keep the two
 * in sync (and the courier rules in apps/api/src/store/delivery-window.ts).
 *
 * Law: 6h minimum between placing a distance order and handing alcohol over,
 * and handover only Mon–Sat 10:00–20:00 / Sun 10:00–18:00. The slot must also
 * fall inside the chosen store's own opening hours; otherwise it rolls to the
 * next open day. Store hours mirror the storefront's lib/company.ts — update
 * both if a shop's hours change.
 */

const TZ = "Europe/Riga";
const LEAD_MS = 6 * 60 * 60 * 1000;

const LEGAL_OPEN_MIN = 10 * 60;
const LEGAL_CLOSE_WEEKDAY_MIN = 20 * 60;
const LEGAL_CLOSE_SUNDAY_MIN = 18 * 60;

/** [weekdays, saturday, sunday] in minutes past midnight; null = closed. */
type DayRange = [number, number] | null;
interface StoreHours {
  match: string[];
  weekdays: DayRange;
  saturday: DayRange;
  sunday: DayRange;
}

const STORE_HOURS: StoreHours[] = [
  {
    match: ["Baznīcas"],
    weekdays: [11 * 60, 19 * 60],
    saturday: [11 * 60, 17 * 60],
    sunday: null,
  },
  {
    match: ["Brīvības"],
    weekdays: [11 * 60, 19 * 60],
    saturday: [11 * 60, 17 * 60],
    sunday: null,
  },
  {
    match: ["Duntes", "Sky&More"],
    weekdays: [10 * 60, 20 * 60],
    saturday: [10 * 60, 20 * 60],
    sunday: [10 * 60, 18 * 60],
  },
];

const WEEKDAY_INDEX: Record<string, number> = {
  Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6,
};

interface RigaParts {
  year: number;
  month: number;
  day: number;
  minutes: number;
  weekday: number;
}

function rigaParts(instant: Date): RigaParts {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: TZ,
    hour12: false,
    weekday: "short",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).formatToParts(instant);
  const map: Record<string, string> = {};
  for (const p of parts) map[p.type] = p.value;
  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    minutes: (Number(map.hour) % 24) * 60 + Number(map.minute),
    weekday: WEEKDAY_INDEX[map.weekday ?? ""] ?? 0,
  };
}

function rigaWallClockToUtc(
  year: number,
  month: number,
  day: number,
  minutes: number,
): Date {
  const guess = Date.UTC(year, month - 1, day, Math.floor(minutes / 60), minutes % 60);
  const p = rigaParts(new Date(guess));
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, Math.floor(p.minutes / 60), p.minutes % 60);
  return new Date(guess - (asUtc - guess));
}

function handoverWindow(store: StoreHours | null, weekday: number): [number, number] | null {
  const legalClose = weekday === 0 ? LEGAL_CLOSE_SUNDAY_MIN : LEGAL_CLOSE_WEEKDAY_MIN;
  const storeRange: DayRange = store
    ? weekday === 0
      ? store.sunday
      : weekday === 6
        ? store.saturday
        : store.weekdays
    : [LEGAL_OPEN_MIN, legalClose]; // unknown store → legal window only
  if (!storeRange) return null;
  const open = Math.max(storeRange[0], LEGAL_OPEN_MIN);
  const close = Math.min(storeRange[1], legalClose);
  return open < close ? [open, close] : null;
}

/**
 * Earliest legal handover instant. `storeHint` is any label naming the store
 * ("Rīga · Baznīcas 39a", the fulfilment store name, …); unknown hints fall
 * back to the bare legal window.
 */
export function earliestPickupHandoverAt(placedAt: Date, storeHint: string | null): Date {
  const store =
    (storeHint &&
      STORE_HOURS.find((s) => s.match.some((term) => storeHint.includes(term)))) ||
    null;
  const base = new Date(placedAt.getTime() + LEAD_MS);
  const start = rigaParts(base);
  for (let offset = 0; offset < 8; offset++) {
    const dayAnchor = rigaWallClockToUtc(start.year, start.month, start.day + offset, 12 * 60);
    const p = rigaParts(dayAnchor);
    const win = handoverWindow(store, p.weekday);
    if (!win) continue;
    if (offset === 0) {
      if (start.minutes <= win[1]) {
        return start.minutes < win[0]
          ? rigaWallClockToUtc(p.year, p.month, p.day, win[0])
          : base;
      }
      continue;
    }
    return rigaWallClockToUtc(p.year, p.month, p.day, win[0]);
  }
  return base;
}
